import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Save } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Button } from '../../ui/Button';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { DetailField } from '../course/DetailField';
import { mockData } from '../../../services/mockData';
import { formatEuro, parseAmount } from '../../../utils/currency';
import { formatCourseDate } from '../../../utils/trainingCourses';
import { withVat } from '../../../utils/trainingBilling';
import { InvoiceDocument } from '../../../types/trainingBilling';
interface InvoiceSectionProps {
  invoice: InvoiceDocument;
  // Company in the registry: its contracts are proposed first
  companyName?: string;
  issuedProformaNumbers: string[];
  onSave: (invoice: InvoiceDocument) => void;
}
export function InvoiceSection({
  invoice,
  companyName,
  issuedProformaNumbers,
  onSave
}: InvoiceSectionProps) {
  const [draft, setDraft] = useState(invoice);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => setDraft(invoice), [invoice]);

  // Contracts from "Offerte e contratti"; only the company's when it is in the registry
  const contracts = useMemo(() => {
    const all = mockData.getContracts();
    const own = companyName ? all.filter(c => c.client === companyName) : [];
    return own.length ? own : all;
  }, [companyName]);
  const update = (changes: Partial<InvoiceDocument>) => {
    setDraft(d => ({
      ...d,
      ...changes
    }));
    setSaved(false);
    setErrors(e => {
      const next = {
        ...e
      };
      Object.keys(changes).forEach(k => delete next[k]);
      return next;
    });
  };
  const save = () => {
    const next: Record<string, string> = {};
    if (draft.issueDate && draft.dueDate && draft.dueDate < draft.issueDate) next.dueDate = 'La scadenza non può precedere l’emissione';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave({
      ...draft,
      number: draft.number?.trim() || undefined
    });
    setSaved(true);
  };
  return <Card title="Fattura" noHoverShadow data-id="element-1038" data-mp="2aiweqhze0lnm">
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 mb-6" data-id="element-1039" data-mp="1vsfgf7i1ubuy">
        <DetailField label="Numero progressivo" data-id="element-1040" data-mp="20qqpvh4fmkcn">
          <span className="font-mono" data-id="element-1041" data-mp="20pfi767v8sg9">{draft.progressive}</span>
        </DetailField>
        <DetailField label="Prodotto (da contratto)" data-id="element-1042" data-mp="1mqfy8bvat1qf">{draft.contractProduct}</DetailField>
        <DetailField label="Importo con IVA (22%)" data-id="element-1043" data-mp="1qtvpesolm9vq">
          {draft.amount !== undefined && formatEuro(withVat(draft.amount))}
        </DetailField>
        <DetailField label="Data di pagamento" data-id="element-1044" data-mp="1cve8pex41xnb">
          {draft.paymentDate ? formatCourseDate(draft.paymentDate) : <span className="text-slate-400 font-normal" data-id="element-1045" data-mp="378446134newt">
              Automatica con lo stato "Fattura pagata"
            </span>}
        </DetailField>
      </dl>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1046" data-mp="10iahknsk7u1t">
        <SearchableSelect label="Numero di contratto" value={draft.contractNumber || ''} onChange={e => {
        const contract = contracts.find(c => c.number === e.target.value);
        update({
          contractNumber: e.target.value || undefined,
          contractProduct: contract?.product
        });
      }} options={contracts.map(c => ({
        value: c.number,
        label: `${c.number} · ${c.product} · ${c.client}`
      }))} placeholder="Cerca contratto..." description='Collegato al modulo "Offerte e contratti"' data-id="element-1047" data-mp="10gpo71hzeh5j" />
        <Select label="Proforma collegata" value={draft.proformaNumber || ''} onChange={e => update({
        proformaNumber: e.target.value || undefined
      })} options={[{
        value: '',
        label: 'Nessuna proforma'
      }, ...issuedProformaNumbers.map(n => ({
        value: n,
        label: n
      }))]} description="Scelta tra le proforma emesse" data-id="element-1048" data-mp="1o84t92157ckh" />
        <Input label="Importo senza IVA" type="number" min={0} step="0.01" value={draft.amount ?? ''} onChange={e => update({
        amount: parseAmount(e.target.value)
      })} endContent={<span className="text-xs" data-id="element-1050" data-mp="2v0hqv3lspv13">€</span>} data-id="element-1049" data-mp="2m0uoofl8sl6z" />
        <Input label="Numero fattura" value={draft.number || ''} onChange={e => update({
        number: e.target.value
      })} placeholder="es. FT 410/2026" description="Con il numero la fattura risulta effettuata" data-id="element-1051" data-mp="1yd4gii6nbzo2" />
        <Input label="Data di emissione" type="date" value={draft.issueDate || ''} onChange={e => update({
        issueDate: e.target.value || undefined
      })} description="Compilata in automatico, modificabile" data-id="element-1052" data-mp="2c3q1ccfnujol" />
        <Input label="Data di scadenza" type="date" value={draft.dueDate || ''} onChange={e => update({
        dueDate: e.target.value || undefined
      })} error={errors.dueDate} data-id="element-1053" data-mp="1jlvqi7yu0cae" />
      </div>
      <div className="flex items-center justify-end gap-3 mt-6" data-id="element-1054" data-mp="132zhnyh5phux">
        {saved && <span role="status" className="flex items-center gap-1.5 text-sm text-green-700" data-id="element-1055" data-mp="y450t22r23dg">
            <CheckCircle2 className="h-4 w-4" data-id="element-1056" data-mp="y6heh31h5rkp" /> Fattura salvata
          </span>}
        <Button startContent={<Save className="h-4 w-4" data-id="element-1058" data-mp="oebqe4ecfuas" />} onClick={save} data-id="element-1057" data-mp="t74vwuhmz6nx">
          Salva fattura
        </Button>
      </div>
    </Card>;
}